// stores/useAuthStore.ts
import { defineStore } from 'pinia';
import { resetAllStores } from '~/composables/useStoreReset';
import { useMenuStore } from '~/stores/useMenuStore';
import {
  type CheckAuthFailureReason,
  isFetchNetworkError,
  lastResponseWasSessionExpired,
  showSessionExpiredModalIfNeeded,
} from '~/composables/useSessionAuth';
import { notifyApiError } from '~/composables/useApiToast';
import {
  LOGIN_STORAGE_KEYS,
  persistLoginUserToStorage,
  clearLoginUserStorage,
} from '~/composables/useLoginSuccess';
import useNotificationApi from '~/composables/notifications/useNotificationApi';
import {
  clearAttachmentMaxFileSize,
  restoreAttachmentMaxFileSizeFromStorage,
  setAttachmentMaxFileSize,
} from '~/composables/attachment/useAttachmentApi';

/** ofetch FetchError 등 HTTP 응답이 있는 실패 (useApi onResponseError에서 이미 토스트 표시) */
const fetchErrorHasHttpResponse = (error: unknown): boolean =>
  error != null &&
  typeof error === 'object' &&
  'response' in error &&
  (error as { response?: unknown }).response != null;

/** 로그인 화면에서 정상 로그인 직후: asis↔tobe 공유 키를 비워 첫 진입은 핀 해제·키 미존재로 맞춤 */
function clearSidebarPinnedStorageForLoginUser(compId: string | undefined, userId: string | undefined) {
  if (!import.meta.client || !compId || !userId) return;
  const key = `sgate.sidebarPinned.${compId}.${userId}`;
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    // storage 접근 불가(private mode 등) 시 무시
  }
}

const COMP_ICON_YN_STORAGE_KEY = LOGIN_STORAGE_KEYS.compIconYn;
const SSO_SERVICE_LIST_STORAGE_KEY = 'ssoServiceList';
const SHOW_LOGIN_POP_NOTICE_STORAGE_KEY = 'sgate:showLoginPopNotice';

export type SsoServiceItem = {
  apiServiceId: string;
  apiServiceNm: string;
};

/** 로그인 응답 compIconYn을 localStorage에 저장 (로그인 시에만 호출) */
function persistCompIconYnToStorage(value: string) {
  if (!import.meta.client) return;
  try {
    localStorage.setItem(COMP_ICON_YN_STORAGE_KEY, value ?? '');
  } catch {
    // storage 접근 불가 시 무시
  }
}

/** 새로고침·세션 복구 시 localStorage에서 compIconYn 복원 */
function loadCompIconYnFromStorage(): string {
  if (!import.meta.client) return '';
  try {
    return localStorage.getItem(COMP_ICON_YN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function removeCompIconYnFromStorage() {
  if (!import.meta.client) return;
  try {
    localStorage.removeItem(COMP_ICON_YN_STORAGE_KEY);
  } catch {
    // storage 접근 불가 시 무시
  }
}

function normalizeSsoServiceList(raw: unknown): SsoServiceItem[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item: any) => {
      const apiServiceId = String(item?.apiServiceId ?? item?.API_SERVICE_ID ?? '').trim();
      const apiServiceNm = String(item?.apiServiceNm ?? item?.API_SERVICE_NM ?? '').trim();
      if (!apiServiceId || !apiServiceNm) return null;
      return { apiServiceId, apiServiceNm };
    })
    .filter((item): item is SsoServiceItem => item != null);
}

function persistSsoServiceListToStorage(list: SsoServiceItem[]) {
  if (!import.meta.client) return;
  try {
    localStorage.setItem(SSO_SERVICE_LIST_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // storage 접근 불가 시 무시
  }
}

function loadSsoServiceListFromStorage(): SsoServiceItem[] {
  if (!import.meta.client) return [];
  try {
    const saved = localStorage.getItem(SSO_SERVICE_LIST_STORAGE_KEY);
    if (!saved) return [];
    return normalizeSsoServiceList(JSON.parse(saved));
  } catch {
    return [];
  }
}

function removeSsoServiceListFromStorage() {
  if (!import.meta.client) return;
  try {
    localStorage.removeItem(SSO_SERVICE_LIST_STORAGE_KEY);
  } catch {
    // storage 접근 불가 시 무시
  }
}

/** loginProcess.do JSON — ssoServiceList 반영 */
function applyLoginSsoServiceList(res: { ssoServiceList?: unknown } | null | undefined) {
  return normalizeSsoServiceList(res?.ssoServiceList);
}

/** 로그인 직후 헤더에서 공지 팝업 1회 조회용 플래그 */
function markLoginPopNoticePending() {
  if (!import.meta.client) return;
  try {
    sessionStorage.setItem(SHOW_LOGIN_POP_NOTICE_STORAGE_KEY, 'Y');
  } catch {
    // storage 접근 불가 시 무시
  }
}

/**
 * 권한 코드 목록 정규화
 * - authorities: 로그인/세션 API 표준 필드 (문자열 '01,03' 또는 string[])
 * - adminGubunList: loginProcess.do / auth/me user 객체에 실전달되는 권한 목록
 */
function normalizeAuthorities(source: {
  authorities?: unknown;
  adminGubunList?: unknown;
  user?: { authorities?: unknown; adminGubunList?: unknown } | null;
}): string[] {
  const raw =
    source.authorities ??
    source.adminGubunList ??
    source.user?.authorities ??
    source.user?.adminGubunList;

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((a: string) => a.trim())
      .filter((a: string) => a);
  }
  if (Array.isArray(raw)) {
    return raw.map(a => String(a).trim()).filter(a => a);
  }
  return [];
}

/** loginProcess.do JSON — compId별 첨부 총 용량(maxTotalSize) 반영 */
function applyLoginMaxTotalSize(res: { maxTotalSize?: unknown } | null | undefined) {
  if (res?.maxTotalSize != null && res.maxTotalSize !== '') {
    setAttachmentMaxFileSize(res.maxTotalSize);
  }
}

function clearLoginMaxTotalSize() {
  clearAttachmentMaxFileSize();
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as null | {
      compId: string;
      userId: string;
      userNm: string;
      mainPgmId: string;
      mainPgmPath: string;
      mainPgmNm: string;
      authMethod: string;
      alertPwChangeYn: string; // 비밀번호 변경 주기 도래 알림 (연장 가능)
      pwInitYn: string; // 반드시 비밀번호 변경 필요 (연장 불가)
      sessionLeftTime?: number;
      unreadNotificationCnt?: number;
    }, // 사용자 정보
    compIconYn: '' as string,
    /** SSO 연동서비스 목록 (loginProcess.do ssoServiceList) */
    ssoServiceList: [] as SsoServiceItem[],
    isLoggedIn: false,
    // 사용자 권한 목록 (예: ['01', '03', '12'])
    authorities: [] as string[],
    /** checkAuth 실패 시 원인 (미들웨어·UI 분기용) */
    lastCheckAuthReason: null as CheckAuthFailureReason | null,
  }),

  getters: {
    /**
     * 사용자가 주어진 권한 중 하나라도 가지고 있는지 확인
     * @example hasAnyAuthority(['01', '03', '12']) // true if user has any of these
     */
    hasAnyAuthority:
      state =>
      (...authCodes: string[]): boolean => {
        if (!state.authorities || state.authorities.length === 0) {
          return false;
        }
        return authCodes.some(code => state.authorities.includes(code));
      },

    /**
     * 사용자가 주어진 권한을 모두 가지고 있는지 확인
     * @example hasAllAuthorities(['01', '03']) // true only if user has both
     */
    hasAllAuthorities:
      state =>
      (...authCodes: string[]): boolean => {
        if (!state.authorities || state.authorities.length === 0) {
          return false;
        }
        return authCodes.every(code => state.authorities.includes(code));
      },

    /**
     * 사용자가 특정 권한을 가지고 있는지 확인
     * @example hasAuthority('01') // true if user has '01'
     */
    hasAuthority:
      state =>
      (authCode: string): boolean => {
        if (!state.authorities || state.authorities.length === 0) {
          return false;
        }
        return state.authorities.includes(authCode);
      },

    /**
     * 현재 사용자의 권한 목록 반환
     */
    getUserAuthorities: (state): string[] => {
      return state.authorities || [];
    },
  },

  actions: {
    // 로그인 처리
    async login(compId: string, username: string, password: string) {
      try {
        // Spring Security 로그인 엔드포인트 호출 (login.jsp와 동일한 방식)
        const params = new URLSearchParams();
        params.append('userId', username);
        params.append('password', password);
        if (compId) {
          params.append('compId', compId);
        }

        const res: any = await useApi('/api/loginProcess.do', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        });

        // 응답 검증: user 정보가 없거나 에러 응답인 경우 실패 처리
        if (!res || !res.user) {
          this.user = null;
          this.compIconYn = '';
          this.ssoServiceList = [];
          removeCompIconYnFromStorage();
          removeSsoServiceListFromStorage();
          clearLoginMaxTotalSize();
          this.isLoggedIn = false;

          // 서버에서 전달한 에러 메시지 처리
          let errorMessage = '로그인에 실패했습니다.';
          if (res?.errorType === 'locked' || res?.msg?.includes('잠겼습니다')) {
            errorMessage = '비밀번호 실패 횟수를 초과하여 계정이 잠겼습니다.';
          } else if (res?.errorType === 'badCredentials' || res?.msg?.includes('비밀번호')) {
            errorMessage = '아이디 또는 비밀번호가 올바르지 않습니다.';
          } else if (res?.msg) {
            errorMessage = res.msg;
          }

          return { success: false, message: errorMessage };
        }

        clearSidebarPinnedStorageForLoginUser(res.user.compId, res.user.userId);
        this.isLoggedIn = true;
        this.user = res.user;
        this.compIconYn = res.compIconYn ?? '';
        persistCompIconYnToStorage(this.compIconYn);
        persistLoginUserToStorage(res.user);
        this.ssoServiceList = applyLoginSsoServiceList(res);
        persistSsoServiceListToStorage(this.ssoServiceList);
        applyLoginMaxTotalSize(res);
        markLoginPopNoticePending();

        // 권한 정보 저장 (authorities 또는 user.adminGubunList)
        this.authorities = normalizeAuthorities(res);

        return {
          success: true,
          menuListJson: res.menuListJson,
          mainPage: res.mainPage,
          // codeList는 필요시 getCodeList에서 자동으로 fetch됨
        };
      } catch (err: any) {
        this.user = null;
        this.compIconYn = '';
        this.ssoServiceList = [];
        removeCompIconYnFromStorage();
        removeSsoServiceListFromStorage();
        clearLoginMaxTotalSize();
        this.isLoggedIn = false;
        this.authorities = [];

        // 에러 메시지 파싱
        let errorMessage = '로그인에 실패했습니다.';

        // 에러 응답 데이터에서 메시지 추출
        const errorData = err.response?._data || err.data;
        if (errorData?.errorType === 'locked' || errorData?.msg?.includes('잠겼습니다')) {
          errorMessage = '비밀번호 실패 횟수를 초과하여 계정이 잠겼습니다.';
        } else if (errorData?.msg) {
          errorMessage = errorData.msg;
        } else if (err.response?.url?.includes('fail=')) {
          errorMessage = '아이디 또는 비밀번호가 올바르지 않습니다.';
        } else if (err.response?.url?.includes('accessFail=')) {
          errorMessage = '로그인 시도 횟수를 초과했습니다.';
        }

        return { success: false, message: errorMessage };
      }
    },

    // 모바일 네이티브 앱용 토큰 기반 로그인
    async loginWithToken(compId: string, userId: string, token: string) {
      try {
        // 토큰 기반 로그인 엔드포인트 호출
        const params = new URLSearchParams();
        params.append('compId', compId);
        params.append('userId', userId);
        params.append('authToken', token);

        console.log('🔐 [loginWithToken] 요청 시작:', { compId, userId });

        const res: any = await useApi('/api/loginProcess.do', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        });

        console.log('🔐 [loginWithToken] 서버 응답:', {
          hasResponse: !!res,
          hasUser: !!res?.user,
          user: res?.user,
          fullResponse: res,
        });

        // 응답에 user 정보가 없으면 실패 처리
        if (!res || !res.user) {
          console.error('❌ [loginWithToken] 응답에 user 정보 없음:', res);
          this.user = null;
          this.compIconYn = '';
          this.ssoServiceList = [];
          removeCompIconYnFromStorage();
          removeSsoServiceListFromStorage();
          clearLoginMaxTotalSize();
          this.isLoggedIn = false;
          return { success: false, message: '서버에서 사용자 정보를 받지 못했습니다.' };
        }

        clearSidebarPinnedStorageForLoginUser(res.user.compId, res.user.userId);
        this.isLoggedIn = true;
        this.user = res.user;
        this.compIconYn = res.compIconYn ?? '';
        persistCompIconYnToStorage(this.compIconYn);
        persistLoginUserToStorage(res.user);
        this.ssoServiceList = applyLoginSsoServiceList(res);
        persistSsoServiceListToStorage(this.ssoServiceList);
        applyLoginMaxTotalSize(res);
        markLoginPopNoticePending();

        // 권한 정보 저장 (authorities 또는 user.adminGubunList)
        this.authorities = normalizeAuthorities(res);

        console.log('✅ [loginWithToken] 로그인 성공:', {
          userId: this.user.userId,
          userName: this.user.userNm,
          isLoggedIn: this.isLoggedIn,
          authorities: this.authorities,
        });

        return {
          success: true,
          menuListJson: res.menuListJson,
          mainPage: res.mainPage,
        };
      } catch (err: any) {
        this.user = null;
        this.compIconYn = '';
        this.ssoServiceList = [];
        removeCompIconYnFromStorage();
        removeSsoServiceListFromStorage();
        clearLoginMaxTotalSize();
        this.isLoggedIn = false;
        this.authorities = [];

        console.error('❌ [loginWithToken] 예외 발생:', err);

        let errorMessage = '토큰 인증에 실패했습니다.';
        if (err.response?.url?.includes('fail=')) {
          errorMessage = '인증 토큰이 유효하지 않습니다.';
        } else if (err.response?.url?.includes('accessFail=')) {
          errorMessage = '로그인 시도 횟수를 초과했습니다.';
        }

        return { success: false, message: errorMessage };
      }
    },

    // 로그아웃 처리
    async logout() {
      if (process.client) {
        const { notifySessionLogoutToOtherTabs } = await import('~/composables/useSessionMonitor');
        notifySessionLogoutToOtherTabs();
      }

      // 먼저 로그인 상태를 false로 설정하여 세션 만료 다이얼로그 방지
      this.isLoggedIn = false;
      this.user = null;
      this.compIconYn = '';
      this.ssoServiceList = [];
      removeCompIconYnFromStorage();
      clearLoginUserStorage();
      removeSsoServiceListFromStorage();
      clearLoginMaxTotalSize();
      this.authorities = [];

      // 메뉴/구독서비스는 localStorage 에도 남아있어서, 리셋하지 않으면 같은 브라우저에서
      // 다른 계정으로 재로그인할 때 이전 사용자의 메뉴·탭 데이터가 잠깐 노출될 수 있다.
      useMenuStore().$reset();

      try {
        await useApi('/api/logout.do', { method: 'POST' });
      } finally {
        // 브라우저를 완전히 새로고침하여 모든 상태와 API 호출 방지
        if (process.client) {
          // 환경에 따른 base URL 적용 (개발: '/', 프로덕션: '/app')
          const config = useRuntimeConfig();
          const baseURL = config.app.baseURL || '/';
          // '/'로 끝나면 슬래시 제거 (//login 방지)
          const normalizedBase = baseURL === '/' ? '' : baseURL;

          window.location.href = `${normalizedBase}/login`;
        }
      }
    },

    // 유휴 타임아웃 등: 리다이렉트 없이 서버 세션만 무효화한다.
    // (세션 만료 다이얼로그 표시는 호출측에서 담당. 새로고침으로 재로그인되는 것을 막기 위해 서버 세션을 끊는다.)
    async invalidateServerSession() {
      if (process.client) {
        const { notifySessionLogoutToOtherTabs } = await import('~/composables/useSessionMonitor');
        notifySessionLogoutToOtherTabs();
      }

      this.isLoggedIn = false;
      this.user = null;
      this.compIconYn = '';
      this.ssoServiceList = [];
      removeCompIconYnFromStorage();
      removeSsoServiceListFromStorage();
      clearLoginMaxTotalSize();
      this.authorities = [];

      try {
        // 서버 세션 무효화 + JSESSIONID_T 쿠키 삭제 (Spring Security logout)
        await useApi('/api/logout.do', { method: 'POST', skipLoading: true, skipSessionExpiredCheck: true });
      } catch {
        // 이미 만료됐거나 네트워크 오류 시 무시
      }
    },

    // 현재 세션 상태 확인
    // showExpiredDialog: true이면 세션 만료 시에만 다이얼로그 표시 (기본값: true)
    async checkAuth(showExpiredDialog = true): Promise<boolean> {
      this.lastCheckAuthReason = null;

      try {
        console.log('🔍 [checkAuth] /auth/me 요청 시작');

        const userInfo = await useApi('/api/auth/me');

        console.log('🔍 [checkAuth] 서버 응답:', {
          hasUserInfo: !!userInfo,
          userInfo: userInfo,
        });

        if (!userInfo || !userInfo.userId) {
          console.error('❌ [checkAuth] 응답에 사용자 정보 없음:', userInfo);

          if (lastResponseWasSessionExpired.value) {
            this.lastCheckAuthReason = 'session';
            this.user = null;
            this.isLoggedIn = false;
            this.authorities = [];
            if (showExpiredDialog) {
              showSessionExpiredModalIfNeeded();
            }
          } else {
            this.lastCheckAuthReason = 'invalid_response';
            this.user = null;
            this.isLoggedIn = false;
            this.authorities = [];
            if (showExpiredDialog) {
              notifyApiError('오류', '인증 정보를 확인할 수 없습니다.');
            }
          }
          return false;
        }

        const prevUnreadNotificationCnt = this.user?.unreadNotificationCnt;
        this.user = userInfo;
        this.isLoggedIn = true;
        if (userInfo.unreadNotificationCnt == null && prevUnreadNotificationCnt != null) {
          this.user.unreadNotificationCnt = prevUnreadNotificationCnt;
        }

        // 권한 정보 저장 (authorities 또는 adminGubunList)
        this.authorities = normalizeAuthorities(userInfo);

        this.restoreCompIconYnFromStorage();
        this.restoreSsoServiceListFromStorage();
        restoreAttachmentMaxFileSizeFromStorage();

        console.log('✅ [checkAuth] 세션 확인 성공:', {
          userId: this.user.userId,
          userName: this.user.userNm,
          isLoggedIn: this.isLoggedIn,
          authorities: this.authorities,
        });
        return true;
      } catch (error) {
        console.error('❌ [checkAuth] 세션 확인 실패:', error);

        if (lastResponseWasSessionExpired.value) {
          this.lastCheckAuthReason = 'session';
          this.user = null;
          this.isLoggedIn = false;
          this.authorities = [];
          if (showExpiredDialog) {
            showSessionExpiredModalIfNeeded();
          }
          return false;
        }

        if (isFetchNetworkError(error)) {
          this.lastCheckAuthReason = 'network';
          // 통신 오류 시 로그인 상태 유지 (useApi onRequestError에서 토스트 표시)
          return false;
        }

        this.lastCheckAuthReason = 'invalid_response';
        this.user = null;
        this.isLoggedIn = false;
        this.authorities = [];

        // HTTP 4xx/5xx·네트워크 오류는 useApi에서 이미 토스트 표시
        if (showExpiredDialog && !fetchErrorHasHttpResponse(error)) {
          notifyApiError('오류', '인증 확인 중 오류가 발생했습니다.');
        }
        return false;
      }
    },

        /**
     * localStorage에서 compIconYn 복원 (ASIS↔TOBE 전환·새로고침 시 Pinia 초기화 보완)
     * @returns 복원 성공 여부
     */
        restoreCompIconYnFromStorage(): boolean {
          if (!import.meta.client || this.compIconYn) {
            return !!this.compIconYn;
          }
          const saved = loadCompIconYnFromStorage();
          if (saved) {
            this.compIconYn = saved;
            return true;
          }
          return false;
        },

        /**
         * localStorage에서 ssoServiceList 복원 (새로고침 시 Pinia 초기화 보완)
         */
        restoreSsoServiceListFromStorage(): boolean {
          if (!import.meta.client || this.ssoServiceList.length > 0) {
            return this.ssoServiceList.length > 0;
          }
          const saved = loadSsoServiceListFromStorage();
          if (saved.length > 0) {
            this.ssoServiceList = saved;
            return true;
          }
          return false;
        },

    /**
     * 읽지 않은 알림 개수 갱신 (/api/common/selectNotifyInfo.do)
     */
    async refreshUnreadNotificationCnt() {
      if (!this.isLoggedIn || !this.user) return;
      try {
        const res = await useNotificationApi().fetchNotifyInfo();
        const cnt = res?.dataVO?.notifyCnt;
        if (cnt != null) {
          this.user.unreadNotificationCnt = Number(cnt);
        }
      } catch (error) {
        console.error('알림 개수 갱신 실패:', error);
      }
    },

    /**
     * 권한 정보를 직접 설정 (테스트 또는 특수 목적)
     */
    setAuthorities(authorities: string[]) {
      this.authorities = authorities;
    },
  },
});
